import { yupResolver } from '@hookform/resolvers/yup'
import { Button, Chip, Drawer, Stack, Typography } from '@mui/material'
import isEmpty from 'lodash/isEmpty'
import React, { useCallback, useMemo } from 'react'
import {
  type SetValueConfig,
  useForm,
  useFormContext,
  useWatch
} from 'react-hook-form'
import { object as yupObject } from 'yup'

import {
  AddTagsForm,
  type AddTagsFormProps
} from 'components/common/AddTagsForm'
import { ButtonWithConfirmation } from 'components/common/ButtonWithConfirmation'
import {
  ChipListMultiValue,
  type ChipListMultiValueProps
} from 'components/common/chips/ChipList'
import { LoadingButton } from 'components/common/LoadingButton'
import Icon from 'components/Icon'
import {
  validateServiceTagName,
  validateServiceTagValues
} from 'components/services/service-tag-utils'
import {
  formatStatusCheckTag,
  type StatusCheckFormValues
} from 'components/status-checks/status-checks-form-utils'
import { usePopoverState } from 'hooks/use-popover-state'
import toast from 'lib/toast'
import { useGetStatusChecks } from 'queries/status-checks/use-get-status-checks'
import { usePutStatusCheckTags } from 'queries/status-checks/use-put-status-check-tags'
import { handleDataFetchOnChange } from 'utils/handle-data-fetch-on-change'
import { naturalSortBy, unique } from 'utils/list'
import { omit } from 'utils/obj'
import { messageFromResponseError } from 'utils/strings'
import { getMultiSelectTagsSchema } from 'utils/yup-additions'

import type { ServiceStatusCheckFromStatusCheck } from 'types/api/status-checks/status-check'

type TagsFormValues = Pick<StatusCheckFormValues, 'multiSelectTags'>

const tagsFormValidationSchema = yupObject({
  multiSelectTags: getMultiSelectTagsSchema(false)
})

const SUCCESS_MESSAGE = 'Successfully updated tags for Health Check'

const BUTTON_CANCEL_COMMON_PROPS = {
  children: 'Cancel',
  color: 'secondary',
  variant: 'contained'
} as const

function getErrorToast(err?: string) {
  const text =
    err && err.length
      ? err
      : 'There was an issue saving tags for this Health Check. Please try again later.'

  return toast({
    text,
    type: 'error'
  })
}

export const EditTagsDrawer: React.FC<{
  monitorTags?: string[]
  statusCheck: ServiceStatusCheckFromStatusCheck
}> = ({ monitorTags, statusCheck }) => {
  const drawerState = usePopoverState()

  const { refetch: refetchStatusChecks } = useGetStatusChecks({
    enabled: false
  })

  const { mutate: updateStatusCheckTags, isLoading } = usePutStatusCheckTags(
    statusCheck.id
  )

  const statusCheckForm = useFormContext<StatusCheckFormValues>()

  const form = useForm<TagsFormValues>({
    defaultValues: { multiSelectTags: statusCheck.multiSelectTags },
    mode: 'all',
    resolver: yupResolver(tagsFormValidationSchema)
  })
  const { control, formState, reset, setValue } = form
  const { isDirty, isSubmitting, isValid } = formState
  const multiSelectTagsValue = useWatch({ control, name: 'multiSelectTags' })

  const { tags, tagNames, tagValues } = useMemo(() => {
    const newTags = multiSelectTagsValue ?? {}

    return {
      tags: newTags,
      tagNames: Object.keys(newTags),
      tagValues: unique(
        Object.values(newTags).flatMap((tagValues: string[]) => {
          return tagValues.flatMap((tagValue) => {
            return tagValue
          })
        })
      )
    }
  }, [multiSelectTagsValue])

  const handleReset = useCallback(
    (
      values: TagsFormValues['multiSelectTags'] = statusCheck.multiSelectTags
    ) => {
      reset({ multiSelectTags: values })
    },
    [reset, statusCheck.multiSelectTags]
  )

  const setTagsValue = useCallback(
    (values: TagsFormValues['multiSelectTags'], options?: SetValueConfig) => {
      setValue('multiSelectTags', values, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
        ...options
      })
    },
    [setValue]
  )

  const handleCloseOnCancel = () => {
    if (isDirty) {
      handleReset()
    }

    drawerState.close()
  }

  const handleAddTag: AddTagsFormProps['onAdd'] = ({ name, values }) => {
    setTagsValue({
      ...multiSelectTagsValue,
      [name]: naturalSortBy(values)
    })
  }

  const handleDeleteTag: Required<ChipListMultiValueProps>['onDeleteTag'] = ([
    tagName
  ]) => {
    const newTags = omit<TagsFormValues['multiSelectTags']>(
      multiSelectTagsValue,
      tagName
    )

    if (isEmpty(newTags) && isEmpty(statusCheck.multiSelectTags)) {
      /* Handle case where initial multiSelectTags are empty and so submission should be blocked */
      handleReset()
    } else {
      setTagsValue(newTags)
    }
  }

  const updateTags = async () => {
    if (!multiSelectTagsValue) {
      getErrorToast()
      return
    }

    updateStatusCheckTags(multiSelectTagsValue, {
      onError(err) {
        getErrorToast(messageFromResponseError(err))
      },
      async onSuccess() {
        drawerState.close()
        toast({ text: SUCCESS_MESSAGE, type: 'success' })
        handleReset(multiSelectTagsValue)

        // Refresh status checks list
        await handleDataFetchOnChange(refetchStatusChecks)

        /*
         * Make sure the main form has up to date tags
         * so that any further edits are saved properly
         */
        statusCheckForm.setValue('multiSelectTags', multiSelectTagsValue)
        statusCheckForm.resetField('multiSelectTags', {
          defaultValue: multiSelectTagsValue
        })
      }
    })
  }

  const isSavingTags = isSubmitting || isLoading
  const isSavingTagsDisabled = !isDirty || !isValid

  return (
    <>
      <Button
        size="small"
        startIcon={<Icon css={{ width: '.9rem' }} icon="pencil" />}
        variant="contained"
        onClick={drawerState.open}
      >
        Edit
      </Button>
      <Drawer
        anchor="right"
        open={drawerState.isOpen}
        PaperProps={{
          sx: {
            padding: 6,
            height: '100%',
            width: 800
          }
        }}
        {...(!isDirty && { onClose: drawerState.close })}
      >
        <Stack sx={{ rowGap: 3 }}>
          <Typography component="h2" variant="h6">
            Edit Tags
          </Typography>
          <Typography>
            Health Checks tags can be used to annotate identifying information.
            Tags can also be used to help route PNIs. Tags that come from the
            observability tool cannot be modified.
          </Typography>
          <AddTagsForm
            tagNames={tagNames}
            tagValues={tagValues}
            // Note: validation matches the service tag validation
            validateTagName={validateServiceTagName}
            validateTagValues={validateServiceTagValues}
            onAdd={handleAddTag}
          />
          {/* ChipLists wrapper - status check and monitor tags */}
          <Stack
            direction="row"
            sx={[
              {
                alignItems: 'center',
                columnGap: 1,
                flexWrap: 'wrap',
                rowGap: 1
              },
              (muiTheme) => ({ minHeight: muiTheme.spacing(4) })
            ]}
          >
            <ChipListMultiValue
              size="medium"
              tags={tags}
              onDeleteTag={handleDeleteTag}
            />
            {
              /* Note: these are read-only */
              monitorTags?.map((tag) => {
                return (
                  <Chip
                    key={tag}
                    label={formatStatusCheckTag(tag)}
                    role="status"
                  />
                )
              })
            }
          </Stack>
          {/* Drawer actions */}
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              columnGap: 1,
              marginTop: 4
            }}
          >
            <LoadingButton
              data-testid="edit-status-check-save-tags-button"
              isDisabled={isSavingTagsDisabled}
              isLoading={isSavingTags}
              variant="contained"
              onClick={updateTags}
            >
              {isSavingTags ? ' Saving...' : 'Save'}
            </LoadingButton>
            {isDirty ? (
              <ButtonWithConfirmation
                {...BUTTON_CANCEL_COMMON_PROPS}
                buttonCancelText="Keep Editing"
                buttonConfirmText="Discard"
                dialogBodyContent="Do you want to discard unsaved tag changes?"
                dialogTitleContent="Unsaved tag changes"
                onConfirm={handleCloseOnCancel}
              />
            ) : (
              <Button
                {...BUTTON_CANCEL_COMMON_PROPS}
                onClick={handleCloseOnCancel}
              />
            )}
          </Stack>
        </Stack>
      </Drawer>
    </>
  )
}
